/**
 * User-level unit preference (Metric vs Imperial), persisted in
 * localStorage under the `'settings'` key with the shape
 * `{ general: { unit: 'Metric' | 'Imperial' }, ...rest }`.
 *
 * The shape mirrors data360-platform-webui so a flip in either app reads the
 * same value on the same browser. The default is `'Imperial'` because the WPS
 * Studio backend stores telemetry in inches and most legacy forms use ipm/in.
 *
 * Changes emit a `'wps:unitSettingChanged'` window event so listeners on the
 * current page (e.g. Pass Analytics) can re-render without polling, and the
 * native `'storage'` event covers cross-tab synchronisation for free.
 */

import { useCallback, useEffect, useState } from 'react'

export type UnitSetting = 'Metric' | 'Imperial'

const STORAGE_KEY = 'settings'
const CUSTOM_EVENT = 'wps:unitSettingChanged'

const isUnitSetting = (value: unknown): value is UnitSetting =>
  value === 'Metric' || value === 'Imperial'

const readSettings = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed as Record<string, unknown>
  } catch {
    return {}
  }
}

export function getUnitSetting(): UnitSetting {
  const settings = readSettings()
  const general = settings.general as { unit?: unknown } | undefined
  const value = general?.unit
  return isUnitSetting(value) ? value : 'Imperial'
}

export function setUnitSetting(value: UnitSetting): void {
  const settings = readSettings()
  const prevGeneral = (settings.general && typeof settings.general === 'object'
    ? settings.general
    : {}) as Record<string, unknown>
  const next = {
    ...settings,
    general: { ...prevGeneral, unit: value }
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent(CUSTOM_EVENT, { detail: { value } }))
}

/**
 * React hook that returns the current unit setting and a setter. Components
 * subscribe to both the same-tab `'wps:unitSettingChanged'` custom event and
 * the native cross-tab `'storage'` event so flipping the setting anywhere
 * re-renders every consumer immediately.
 */
export function useUnitSetting(): [UnitSetting, (next: UnitSetting) => void] {
  const [value, setValue] = useState<UnitSetting>(() => getUnitSetting())

  useEffect(() => {
    const sync = () => setValue(getUnitSetting())

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) sync()
    }

    window.addEventListener(CUSTOM_EVENT, sync)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(CUSTOM_EVENT, sync)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const set = useCallback((next: UnitSetting) => {
    setUnitSetting(next)
  }, [])

  return [value, set]
}
